import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { overlayVariants, sheetVariants, transition } from '../lib/motion.js';
import { supabase } from '../lib/supabase.js';
import { getClientUid } from '../lib/uid.js';
import { toast } from '../store/useToast.js';

const CATEGORIES = [
  { key: 'bug', label: '🐛 Bug' },
  { key: 'idea', label: '💡 Idée' },
  { key: 'love', label: '❤️ J’adore' },
];

/**
 * Bouton flottant + sheet de feedback invité. Auto-contenu : gère son ouverture,
 * lit l'identité (client_uid) et insère dans public.feedback lui-même.
 */
export default function FeedbackButton({ eventId = null, barUserId = null }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (open) {
      setCategory(null);
      setMessage('');
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape' && open && !submitting) setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting]);

  async function submit() {
    if (submittingRef.current) return;
    const msg = message.trim();
    if (!msg) {
      toast.error('Écris un petit mot d’abord 🙂');
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    const { error } = await supabase.from('feedback').insert({
      event_id: eventId ?? null,
      bar_user_id: barUserId ?? null,
      client_uid: getClientUid(),
      category,
      message: msg.slice(0, 2000),
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : null,
    });
    if (error) {
      const detail = import.meta.env.DEV ? ` (${error.message})` : '';
      toast.error(`Envoi impossible, réessaie.${detail}`);
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }
    toast.success('Merci ! Bien reçu 🙏');
    setOpen(false);
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Donner mon avis"
        className="fixed right-4 z-40 flex h-11 items-center gap-1.5 rounded-full border border-border bg-bg-card/90 px-3.5 text-xs font-semibold text-white/80 shadow-lg backdrop-blur transition hover:bg-white/10"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 92px)' }}
      >
        💬 Avis
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/75 p-0 backdrop-blur-md sm:items-center sm:p-4"
            onClick={() => {
              if (!submitting) setOpen(false);
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Donner mon avis"
            variants={overlayVariants}
            initial={reduceMotion ? false : 'initial'}
            animate="animate"
            exit={reduceMotion ? undefined : 'exit'}
            transition={transition(reduceMotion, 'fast')}
          >
            <motion.div
              className="sheet-surface max-h-[92dvh] w-full max-w-[480px] overflow-y-auto rounded-t-3xl border border-border p-5 pb-[max(env(safe-area-inset-bottom),1.25rem)] sm:rounded-2xl"
              onClick={(e) => e.stopPropagation()}
              variants={sheetVariants}
              initial={reduceMotion ? false : 'initial'}
              animate="animate"
              exit={reduceMotion ? undefined : 'exit'}
              transition={transition(reduceMotion, 'panel')}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-mono text-[11px] font-semibold uppercase tracking-widest text-accent">
                    Ton avis compte
                  </p>
                  <p className="mt-1 text-base font-semibold text-white">Un bug, une idée ?</p>
                  <p className="mt-1 text-xs leading-relaxed text-white/55">
                    Dis-nous tout — ça nous aide à améliorer la soirée en direct.
                  </p>
                </div>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => !submitting && setOpen(false)}
                  className="rounded-xl border border-border px-3 py-1.5 text-xs text-white/70 hover:bg-white/5 disabled:opacity-40"
                >
                  Fermer
                </button>
              </div>

              <div className="mt-4 flex gap-2">
                {CATEGORIES.map((c) => {
                  const sel = category === c.key;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      disabled={submitting}
                      onClick={() => setCategory(sel ? null : c.key)}
                      className={`min-h-[40px] flex-1 rounded-xl border px-2 py-2 text-xs font-semibold transition ${
                        sel
                          ? 'border-accent bg-accent/20 text-white'
                          : 'border-border bg-white/[0.04] text-white/75 hover:bg-white/[0.08]'
                      }`}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>

              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={2000}
                rows={4}
                placeholder="Ton message…"
                disabled={submitting}
                className="input mt-3 w-full resize-none"
              />

              <button
                type="button"
                disabled={submitting}
                onClick={submit}
                className="btn-primary mt-4 min-h-[44px] w-full py-3 text-sm"
              >
                {submitting ? '…' : 'Envoyer'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
